import { useTranslation } from 'react-i18next';
import './discontinuation-details.component.scss';

interface DiscontinuationDetailsProps {
    reasonType: string;
    reasonDescription: string;
    isCurrentlyWorking?: boolean;
    onReasonTypeChange: (type: string) => void;
    onReasonDescriptionChange: (description: string) => void;
}

export const DiscontinuationDetails: React.FC<DiscontinuationDetailsProps> = ({
    reasonType,
    reasonDescription,
    isCurrentlyWorking = false,
    onReasonTypeChange,
    onReasonDescriptionChange
}) => {
    const { t } = useTranslation();

    // Swedish labels — always sent in payload regardless of UI language
    const swedishLabels: Record<string, string> = {
        '':                  '',
        'contract-ended':    'Kontraktet avslutades',
        'resignation':       'Uppsägning av anställd',
        'termination':       'Uppsägning av arbetsgivare',
        'mutual-agreement':  'Ömsesidig överenskommelse',
        'end-of-assignment': 'Uppdragets slut',
        'retirement':        'Pension',
        'redundancy':        'Övertalighet / Avskedning',
        'other':             'Annan orsak',
    };

    // Reverse-map: Swedish label → key (so we can restore the key from parent state)
    const getKeyFromSwedishLabel = (label: string): string => {
        const entry = Object.entries(swedishLabels).find(([, v]) => v === label);
        return entry ? entry[0] : label;
    };

    const reasonTypes = [
        { value: '', label: t('Select reason') },
        { value: 'contract-ended', label: t('Contract ended') },
        { value: 'resignation', label: t('Resignation by employee') },
        { value: 'termination', label: t('Termination by employer') },
        { value: 'mutual-agreement', label: t('Mutual agreement') },
        { value: 'end-of-assignment', label: t('End of assignment') },
        { value: 'retirement', label: t('Retirement') },
        { value: 'redundancy', label: t('Redundancy / Laid off') },
        { value: 'other', label: t('Other reason') },
    ];

    // The parent may store either a key or a Swedish label — resolve to key for dropdown
    const resolvedKey = getKeyFromSwedishLabel(reasonType) || reasonType;
    const isKnownReason = resolvedKey in swedishLabels;
    // If value doesn't match any known reason, show 'other' in dropdown
    const selectedKey = isKnownReason ? resolvedKey : (reasonType ? 'other' : '');
    // If unrecognized value came from API, show it in the description field as fallback
    const effectiveDescription = (!isKnownReason && reasonType)
        ? (reasonDescription || reasonType)
        : reasonDescription;

    const handleReasonChange = (key: string) => {
        // For 'other', keep key as-is so parent payload sends reasonDescription
        // For all other reasons, pass Swedish label for payload
        onReasonTypeChange(key === 'other' ? 'other' : (swedishLabels[key] ?? key));
    };

    return (
        <div className="discontinuation-details">
            <div className="discontinuation-details__card">
                <div className="discontinuation-details__header">
                    <h3 className="discontinuation-details__title">{t('Discontinuation Details')}</h3>
                    <p className="discontinuation-details__description">
                        {t('Reason why the employment has ended in whole or in part')}
                    </p>
                </div>
                
                <div className="discontinuation-details__body">
                    <div className="discontinuation-details__form">
                        <div className="discontinuation-details__field-row">
                            <div className="discontinuation-details__field">
                                <label className="discontinuation-details__label">
                                    {t('Reason type')}
                                </label>
                                <div
                                    className={`discontinuation-details__select-wrapper${isCurrentlyWorking ? ' discontinuation-details__select-wrapper--disabled' : ''}`}
                                    title={isCurrentlyWorking ? t('You are currently employed, so you cannot add a reason') : undefined}
                                >
                                    <select
                                        className="discontinuation-details__select"
                                        value={isCurrentlyWorking ? '' : selectedKey}
                                        disabled={isCurrentlyWorking}
                                        onChange={(e) => handleReasonChange(e.target.value)}
                                    >
                                        {reasonTypes.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                    <span className="discontinuation-details__select-icon">
                                        <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M1 1.5L6 6.5L11 1.5" stroke="#68727D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </span>
                                </div>
                            </div>
                            {selectedKey === 'other' && !isCurrentlyWorking && (
                            <div className="discontinuation-details__field">
                                <label className="discontinuation-details__label">
                                    {t('Describe reason')}
                                </label>
                                <input
                                    type="text"
                                    className="discontinuation-details__input"
                                    placeholder={t('Type here...')}
                                    value={effectiveDescription}
                                    onChange={(e) => onReasonDescriptionChange(e.target.value)}
                                />
                            </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
